import { Chip, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import { MouseEvent, useCallback, useMemo, useState } from "react";
import { ActionCell } from "../../components/ActionCell/ActionCell";
import { OneLiner } from "../../components/Table/Table";
import { useHasPermission } from "../../hooks";
import { UserPermissions } from "../../utils/permissions";
import { userFriendlyDateTimestamp } from "../../utils/timestamps";
import { TableMenuItemInterface } from "../../utils/types";
import { ANNUAL_PRODUCT_IDS, MONTHLY_PRODUCT_IDS, PRODUCT_DISPLAY_NAMES, STORE_DISPLAY_NAMES } from "./constants";
import { UserTabEnum } from "./types";

interface Params {
  activeTab: UserTabEnum;
  hoveredRow: number;
  setHoveredRow: (rowIndex: number) => void;
  onClickActivate: () => void;
  onClickInactivate: () => void;
  onClickEditUser: () => void;
  onClickPreview: () => void;
}
export const useGetUsersTableColumns = (params: Params) => {
  const { setHoveredRow, hoveredRow, onClickActivate, onClickInactivate, onClickEditUser, activeTab, onClickPreview } = params;

  const [shouldCloseMenuSelection, setShouldCloseMenuSelection] = useState<boolean>(false);

  const canEditUser = useHasPermission(UserPermissions.EDIT_USERS);
  const canActivateInactivateUsers = useHasPermission(UserPermissions.ACTIVATE_INACTIVATE_USERS);

  const handleClickPreview = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      onClickPreview();
    },
    [onClickPreview, setShouldCloseMenuSelection],
  );

  const handleClickInactivate = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      onClickInactivate();
    },
    [onClickInactivate, setShouldCloseMenuSelection],
  );

  const handleClickActivate = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      onClickActivate();
    },
    [onClickActivate, setShouldCloseMenuSelection],
  );

  const handleClickEditUser = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation?.();
      setShouldCloseMenuSelection(true);
      onClickEditUser();
    },
    [onClickEditUser, setShouldCloseMenuSelection],
  );

  const columns = useMemo(() => {
    const menuItems: TableMenuItemInterface[] = [
      {
        label: "Preview",
        onClick: handleClickPreview,
        visible: true,
      },
      {
        label: "Inactivate",
        onClick: handleClickInactivate,
        visible: activeTab !== UserTabEnum.InActive && canActivateInactivateUsers,
      },
      {
        label: "Activate",
        onClick: handleClickActivate,
        visible: activeTab === UserTabEnum.InActive && canActivateInactivateUsers,
      },
      {
        label: "Edit",
        onClick: handleClickEditUser,
        visible: canEditUser,
      },
    ];

    return [
      {
        accessorKey: "id",
        header: "User ID",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={row.original.id} />,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={row.original.name} />,
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={row.original.email} />,
      },
      {
        accessorKey: "roles",
        header: "Roles",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }: any) => (
          <Stack sx={{ cursor: "pointer" }} direction="row" spacing={1}>
            {row.original?.roles?.length ? (
              row.original?.roles?.map(({ role, id }: any) => {
                return <Chip size="small" key={id} label={role} />;
              })
            ) : (
              <></>
            )}
          </Stack>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={userFriendlyDateTimestamp(row.original.createdAt)} />,
      },
      {
        accessorKey: "updatedAt",
        header: "Updated At",
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={userFriendlyDateTimestamp(row.original.updatedAt)} />,
      },
      {
        accessorKey: "status",
        header: "Status",
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={row.original.status} />,
      },
      {
        accessorKey: "onboardingStatus",
        header: "Onboarding Completed",
        enableColumnFilter: true,
        Filter: ({ column }: any) => {
          const handleChange = (event: SelectChangeEvent<string>) => {
            column.setFilterValue(event.target.value || undefined);
          };

          return (
            <Select value={column.getFilterValue() ?? ""} onChange={handleChange} displayEmpty size="small" fullWidth>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          );
        },
        filterFn: (row: any, id: string, filterValue: string) => {
          if (!filterValue) return true;
          const onboardingCompleted = row.original.profile?.onboardingCompleted;
          if (filterValue === "true") return !!onboardingCompleted;
          return !onboardingCompleted;
        },
        Cell: ({ row }: any) => <OneLiner text={row.original.profile?.onboardingCompleted ? "Yes" : "No"} />,
      },
      {
        accessorKey: "lastLoginAt",
        header: "Last Login",
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={userFriendlyDateTimestamp(row.original.lastLoginAt)} />,
      },
      {
        accessorKey: "mostRecentLoginCity",
        header: "Login City",
        enableColumnFilter: true,
        Cell: ({ row }: any) => <OneLiner text={row.original.mostRecentLoginCity || "-"} />,
        sortDescFirst: false,
      },
      {
        accessorKey: "mostRecentLoginCountry",
        header: "Login Country",
        enableColumnFilter: true,
        Cell: ({ row }: any) => <OneLiner text={row.original.mostRecentLoginCountry || "-"} />,
        sortDescFirst: false,
      },
      {
        accessorKey: "mostRecentLoginDevice",
        header: "Login Device",
        enableColumnFilter: true,
        Filter: ({ column }: any) => {
          const handleChange = (event: SelectChangeEvent<string>) => {
            column.setFilterValue(event.target.value || undefined);
          };

          return (
            <Select value={column.getFilterValue() ?? ""} onChange={handleChange} displayEmpty size="small" fullWidth>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="iOS">iOS</MenuItem>
              <MenuItem value="Android">Android</MenuItem>
            </Select>
          );
        },
        Cell: ({ row }: any) => <OneLiner text={row.original.mostRecentLoginDevice || "-"} />,
        sortDescFirst: false,
      },
      {
        accessorKey: "totalCulturesSubscribed",
        header: "Cultures",
        enableColumnFilter: false,
        Cell: ({ row }: any) => {
          const totalCulturesSubscribed = row.original.totalCulturesSubscribed ?? 0;
          return <OneLiner text={totalCulturesSubscribed} />;
        },
        sortDescFirst: false,
      },
      {
        accessorKey: "totalTagsSubscribed",
        header: "Interests",
        enableColumnFilter: false,
        Cell: ({ row }: any) => {
          const totalTagsSubscribed = row.original.totalTagsSubscribed ?? 0;
          return <OneLiner text={totalTagsSubscribed} />;
        },
        sortDescFirst: false,
      },
      {
        accessorKey: "subscribedAt",
        header: "Subscribed",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={userFriendlyDateTimestamp(row.original.subscribedAt)} />,
      },
      {
        accessorKey: "subscriptionStatus",
        header: "Sub. Status",
        size: 180,
        enableColumnFilter: true,
        Filter: ({ column }: any) => {
          const handleChange = (event: SelectChangeEvent<string>) => {
            column.setFilterValue(event.target.value || undefined);
          };

          return (
            <Select value={column.getFilterValue() ?? ""} onChange={handleChange} displayEmpty size="small" fullWidth>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="trialing">Trialing</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="in_grace_period">In Grace Period</MenuItem>
              <MenuItem value="in_billing_retry">In Billing Retry</MenuItem>
              <MenuItem value="incomplete">Incomplete</MenuItem>
            </Select>
          );
        },
        filterFn: (row: any, _id: string, filterValue: string) => {
          if (!filterValue) return true;
          return row.original.subscriptionStatus === filterValue;
        },
        Cell: ({ row }: any) => (
          <OneLiner sx={{ textTransform: "capitalize" }} text={row.original.subscriptionStatus?.replace(/_/g, " ") || "-"} />
        ),
      },
      {
        accessorKey: "customTrialStatus",
        header: "Custom Trial",
        size: 150,
        enableColumnFilter: true,
        Filter: ({ column }: any) => {
          const handleChange = (event: SelectChangeEvent<string>) => {
            column.setFilterValue(event.target.value || undefined);
          };

          return (
            <Select value={column.getFilterValue() ?? ""} onChange={handleChange} displayEmpty size="small" fullWidth>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="converted">Converted</MenuItem>
            </Select>
          );
        },
        filterFn: (row: any, _id: string, filterValue: string) => {
          if (!filterValue) return true;
          return row.original.customTrial?.status === filterValue;
        },
        Cell: ({ row }: any) => <OneLiner sx={{ textTransform: "capitalize" }} text={row.original.customTrial?.status ?? "-"} />,
      },
      {
        accessorKey: "subscriptionProductId",
        header: "Product",
        size: 150,
        enableColumnFilter: true,
        Filter: ({ column }: any) => {
          const handleChange = (event: SelectChangeEvent<string>) => {
            column.setFilterValue(event.target.value || undefined);
          };

          return (
            <Select value={column.getFilterValue() ?? ""} onChange={handleChange} displayEmpty size="small" fullWidth>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Annual">Annual</MenuItem>
            </Select>
          );
        },
        filterFn: (row: any, _id: string, filterValue: string) => {
          if (!filterValue) return true;
          const pid = row.original.subscriptionProductId;
          if (filterValue === "Monthly") return MONTHLY_PRODUCT_IDS.includes(pid);
          if (filterValue === "Annual") return ANNUAL_PRODUCT_IDS.includes(pid);
          return false;
        },
        Cell: ({ row }: any) => (
          <OneLiner text={PRODUCT_DISPLAY_NAMES[row.original.subscriptionProductId] ?? row.original.subscriptionProductId ?? "-"} />
        ),
      },
      {
        accessorKey: "subscriptionStore",
        header: "Store",
        size: 130,
        enableColumnFilter: true,
        Filter: ({ column }: any) => {
          const handleChange = (event: SelectChangeEvent<string>) => {
            column.setFilterValue(event.target.value || undefined);
          };

          return (
            <Select value={column.getFilterValue() ?? ""} onChange={handleChange} displayEmpty size="small" fullWidth>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="app_store">App Store</MenuItem>
              <MenuItem value="play_store">Play Store</MenuItem>
              {!process.env.REACT_APP_API?.includes("admin") && <MenuItem value="test_store">Test Store</MenuItem>}
            </Select>
          );
        },
        filterFn: (row: any, _id: string, filterValue: string) => {
          if (!filterValue) return true;
          return row.original.subscriptionStore === filterValue;
        },
        Cell: ({ row }: any) => (
          <OneLiner text={STORE_DISPLAY_NAMES[row.original.subscriptionStore] ?? row.original.subscriptionStore ?? "-"} />
        ),
      },
      {
        accessorKey: "subscriptionStartAt",
        header: "Billing Start",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={userFriendlyDateTimestamp(row.original.subscriptionStartAt)} />,
      },
      {
        accessorKey: "subscriptionEndAt",
        header: "Billing End",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={userFriendlyDateTimestamp(row.original.subscriptionEndAt)} />,
      },
      {
        accessorKey: "unsubscribedAt",
        header: "Unsubscribed",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={userFriendlyDateTimestamp(row.original.unsubscribedAt)} />,
      },
      {
        accessorKey: "totalShares",
        header: "Shares",
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={row.original.totalShares ?? 0} />,
      },
      {
        accessorKey: "totalLikes",
        header: "Likes",
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={row.original.totalLikes ?? 0} />,
      },
      {
        accessorKey: "totalHides",
        header: "Hides",
        enableColumnFilter: false,
        Cell: ({ row }: any) => <OneLiner text={row.original.totalHides ?? 0} />,
      },
      {
        accessorKey: "action",
        id: "action",
        size: 50,
        header: "",
        enableResizing: false,
        enableColumnFilter: false,
        muiTableHeadCellProps: () => ({
          sx: {
            visibility: "hidden",
          },
        }),
        Cell: ({ row }: any) => {
          if (hoveredRow !== row.index) return null;
          return (
            <ActionCell
              shouldCloseMenuSelection={shouldCloseMenuSelection}
              setShouldCloseMenuSelection={setShouldCloseMenuSelection}
              hovered={hoveredRow === row.index}
              loading={false}
              activeTab={activeTab}
              onActionButtonClick={(event: MouseEvent<HTMLElement>) => {
                event?.stopPropagation?.();
                setHoveredRow(row.index);
              }}
            >
              {menuItems.map(
                (item, index) =>
                  item.visible && (
                    <MenuItem key={index} sx={item.sx} onClick={item.onClick}>
                      {item.label}
                    </MenuItem>
                  ),
              )}
            </ActionCell>
          );
        },
      },
    ];
  }, [
    handleClickPreview,
    handleClickInactivate,
    activeTab,
    canActivateInactivateUsers,
    handleClickActivate,
    handleClickEditUser,
    canEditUser,
    shouldCloseMenuSelection,
    hoveredRow,
    setHoveredRow,
  ]);

  return columns;
};
